import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, Shield } from 'lucide-react';
import { LP_URL } from '../../lib/utils';
import { LatestArticleCard } from './LatestArticleCard';

export const HeroSection = ({ t, heroImage, heroFit, heroPosition, latestPost }) => (
  <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
    <div className="absolute inset-0 bg-slate-900">
      {heroImage && (
        <img
          src={heroImage}
          alt="Contabilidade médica especializada"
          className="w-full h-full"
          style={{ objectFit: heroFit, objectPosition: heroPosition }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/60" />
    </div>
    <div className="container-apex relative z-10 py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:items-center">
        <div className="lg:col-span-8 max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-[#007f73]/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 animate-fade-in border border-[#5cc1b0]/30">
            <Shield className="w-4 h-4 text-[#5cc1b0]" />
            <span className="text-[#5cc1b0] text-sm font-medium">{t('hero-badge', 'Contabilidade Especializada para Médicos')}</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 animate-slide-up leading-tight">
            {t('hero-title', 'Pague menos impostos com total segurança')}
          </h1>
          <p className="text-xl text-white/90 mb-4 animate-slide-up leading-relaxed" style={{ animationDelay: '0.1s' }}>
            <strong className="text-[#5cc1b0]">Economia tributária real</strong> {t('hero-subtitle', 'para clínicas e consultórios médicos. Estruturamos seu CNPJ da forma mais eficiente, reduzindo sua carga fiscal de maneira legal e segura.')}
          </p>
          <p className="text-lg text-white/70 mb-8 animate-slide-up" style={{ animationDelay: '0.15s' }}>
            {t('hero-credibility', 'Especialistas em tributação médica há mais de 5 anos, com mais de 100 clínicas atendidas.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <Button
              asChild
              size="lg"
              className="bg-[#5cc1b0] text-slate-900 hover:bg-[#5cc1b0]/90 rounded-md px-5 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-semibold shadow-lg w-full sm:w-auto"
              data-testid="hero-cta-primary"
            >
              <a href={LP_URL} target="_blank" rel="noopener noreferrer">
                {t('hero-cta-primary', 'Quero pagar menos impostos')}
                <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-transparent text-white border-white/40 hover:bg-white/10 rounded-md px-5 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-medium w-full sm:w-auto"
              data-testid="hero-cta-secondary"
            >
              <Link to="/servicos">
                {t('hero-cta-secondary', 'Conhecer todos os serviços')}
              </Link>
            </Button>
          </div>
        </div>
        {latestPost && (
          <div className="lg:col-span-4 animate-fade-in hidden lg:block" style={{ animationDelay: '0.3s' }}>
            <LatestArticleCard post={latestPost} />
          </div>
        )}
      </div>
    </div>
  </section>
);

export const StatsBar = ({ t }) => (
  <section className="py-8 bg-[#1e4e7e]">
    <div className="container-apex">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
        {[
          { num: 'stat-1-number', label: 'stat-1-label', dNum: '100+', dLabel: 'Clínicas atendidas' },
          { num: 'stat-2-number', label: 'stat-2-label', dNum: '5+ anos', dLabel: 'De experiência' },
          { num: 'stat-3-number', label: 'stat-3-label', dNum: '30%', dLabel: 'Economia média em impostos' },
          { num: 'stat-4-number', label: 'stat-4-label', dNum: '98%', dLabel: 'Satisfação dos clientes' },
        ].map((s, i) => (
          <div key={i}>
            <p className="font-heading font-bold text-3xl text-white">{t(s.num, s.dNum)}</p>
            <p className="text-white/70 text-sm">{t(s.label, s.dLabel)}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);
